// import {ListControllerProps, RaRecord} from 'ra-core';
import {createColumnHelper} from '@tanstack/react-table';
import {DeleteButtonWithConfirmation} from 'components/refinedev/action_buttons';
// import useTable from 'hooks/refinedev/useTable';
import useDialogState from 'hooks/useDialogState';
import DocumentUploadDialog from './document-create-popup';
// import {DocumentItemView} from './components';
import {Button} from '@/shadcn_components/ui/button';
import {CrudFilters} from '@refinedev/core';
import {defaultQueryOptions} from '@/pages/_utils';
import StateBoundary from '@/components/basic/StateBoundary';
import {Tooltip, TooltipContent, TooltipTrigger} from '@/shadcn_components/ui/tooltip';
import formatter from '@/lib/formatter';
import {DataTable} from '@/components/data-table/data-table';
import {DataTableColumnHeader} from '@/components/data-table/data-table-column-header';
import DefaultDataTableToolbar from '@/components/refinedev/data-table/DefaultDataTableToolbar';
import useRefineTanstackDataTable from '@/hooks/refinedev/useRefineTanstackDataTable';
import {useBulkDelete} from '@/components/refinedev/data-table/use-bulk-delete';
import {SelectColumn} from '@/components/data-table/columns';
import {DocumentableObject} from './types';

const thisResource = 'documents';

const columnHelper = createColumnHelper<any>();

const columns = [
  SelectColumn,
  columnHelper.accessor('id', {
    meta: {label: 'ID'},
    header: ({column}) => <DataTableColumnHeader column={column} />,
    cell: info => info.getValue(),
  }),
  columnHelper.accessor('name', {
    meta: {label: 'Tên tệp'},
    header: ({column}) => <DataTableColumnHeader column={column} />,
    cell: info =>
      info.row.original.thumbnails?.thumbnail_small ? (
        <Tooltip>
          <TooltipTrigger>
            <a
              className="hover:text-primary hover:underline"
              href={info.row.original.url}
              target="_blank"
              rel="noopener noreferrer">
              {info.renderValue()}
            </a>
          </TooltipTrigger>
          <TooltipContent className="bg-white border rounded-lg shadow-lg">
            <img
              src={info.row.original.thumbnails?.thumbnail_small}
              alt="preview"
              style={{maxWidth: '200px'}}
            />
          </TooltipContent>
        </Tooltip>
      ) : (
        <a
          className="hover:text-primary hover:underline"
          href={info.row.original.url}
          target="_blank"
          rel="noopener noreferrer">
          {info.renderValue()}
        </a>
      ),
  }),
  columnHelper.accessor('media.extension', {
    meta: {label: 'Loại tệp'},
    header: ({column}) => <DataTableColumnHeader column={column} />,
    cell: info => info.renderValue(),
  }),
  columnHelper.accessor('media.size', {
    meta: {label: 'Kích thước'},
    header: ({column}) => <DataTableColumnHeader column={column} />,
    cell: info => formatter.formatFileSize(info.getValue()),
  }),
  columnHelper.accessor('uploader.name', {
    meta: {label: 'Người tải lên'},
    header: ({column}) => <DataTableColumnHeader column={column} />,
    cell: info => info.renderValue(),
  }),
  columnHelper.display({
    id: 'collection',
    meta: {label: 'Thuộc về'},
    header: ({column}) => <DataTableColumnHeader column={column} />,
    cell: ({row}) => <DocumentCollectionInfo row={row} />,
  }),
  columnHelper.accessor('updated_at', {
    meta: {label: 'Tải lên lúc'},
    header: ({column}) => <DataTableColumnHeader column={column} />,
    cell: info => formatter.formatDateTime(info.getValue()),
  }),
  columnHelper.display({
    id: 'actions',
    meta: {label: 'Hành động'},
    header: ({column}) => <DataTableColumnHeader column={column} />,
    cell: info => <DeleteButtonWithConfirmation resource="documents" id={info.row.original.id} />,
    enableSorting: false,
  }),
];

const DocumentCollectionInfo = ({row}) => {
  const {project, task, department, scope} = row.original;
  if (scope === 'global' && !project && !task && !department) {
    return <div>Chung</div>;
  }
  return (
    <div className="flex flex-col gap-2">
      {project && <span className="text-sm text-muted-foreground">Dự án: {project.name}</span>}
      {task && <span className="text-sm text-muted-foreground">Công việc: {task.name}</span>}
      {department && <span className="text-sm text-muted-foreground">Phòng {department.name}</span>}
    </div>
  );
};

export type DocumentListProps = Partial<DocumentableObject> & {
  scope?: string; // for example: 'global'
  tags?: string;
};

const DocumentList = (props: DocumentListProps) => {
  const {mediable_type, mediable_id} = props;

  // const permanent_filters: CrudFilters | undefined =
  //   mediable_type && mediable_id
  //     ? [{field: mediable_type + '_id', operator: 'eq', value: mediable_id}]
  //     : undefined;

  const {refineTable, tanstackTable} = useRefineTanstackDataTable(
    {
      resource:
        mediable_type && mediable_id
          ? `${mediable_type}s/${mediable_id}/${thisResource}`
          : thisResource,
      sorters: {initial: [{field: 'id', order: 'asc'}]},
      filters: {
        // permanent: permanent_filters,
      },
      queryOptions: defaultQueryOptions,
      syncWithLocation: !mediable_type || !mediable_id,
      meta: {
        append: ['url', 'thumbnails', 'uploader'],
        with: ['project:id,name', 'task:id,name', 'department:id,name'],
      },
    },
    {columns},
  );

  const dialog = useDialogState();

  function popupUpload() {
    dialog.openDialog();
  }
  const bulkDelete = useBulkDelete(thisResource, tanstackTable);

  return (
    <StateBoundary
      isLoading={refineTable.tableQuery.isLoading}
      error={refineTable.tableQuery.error}>
      <div className="p-2">
        <DataTable
          table={tanstackTable}
          toolbar={
            <DefaultDataTableToolbar
              resource={thisResource}
              table={tanstackTable}
              createButton={
                <Button variant="default" size="sm" onClick={popupUpload}>
                  Tải lên
                </Button>
              }
              bulkActions={[bulkDelete]}
            />
          }
        />
        <DocumentUploadDialog
          mediable_type={mediable_type}
          mediable_id={mediable_id}
          dialogState={dialog}
          onSuccess={() => {
            refineTable.tableQuery.refetch();
          }}
        />
      </div>
    </StateBoundary>
  );
};

DocumentList.table_columns = columns;

export default DocumentList;
